import { FileSpreadsheet, PackageOpen, RotateCcw, Undo2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge, Skeleton } from '../../components/shadcn/index.js';
import {
  Button,
  Card,
  Combobox,
  type ComboboxOption,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  Input,
} from '../../components/ui/index.js';
import { useLayoutDirection } from '../../hooks/useDirection.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { localDateStamp } from '../../lib/csv.js';
import { formatDate, formatDateTime, formatNumber } from '../../lib/intl.js';
import { formatMoney, trimMoney } from '../../lib/money.js';
import { fetchAllParties } from '../../lib/parties.js';
import { downloadXlsx, type XlsxValue } from '../../lib/xlsxExport.js';

import { fetchAllReturns, type ReturnRow, type ReturnSource, sumByCurrency } from './returnsData.js';

interface InvoiceRow {
  id: string;
  docNumber: string;
  docDate?: string;
  partyId?: string;
  /** Resolved by the server on list rows — no directory lookup needed. */
  partyName?: string | null;
  status: string;
  currencyCode: string;
  total?: string;
  notes?: string | null;
  isCreditNote?: boolean | 0 | 1;
  creditOfInvoiceId?: string | null;
  warehouseId?: string | null;
}
interface InvoiceLine {
  lineNo: number;
  itemId?: string | null;
  description?: string | null;
  quantity: string;
  unitPrice: string;
  unitName?: string | null;
  unitFactor?: string | null;
}
interface PartyLite { id: string; code: string; displayName: string }
interface WarehouseLite { id: string; code: string; name: string }

const RETURNABLE = new Set(['issued', 'paid']);

/* ------------------------------------------------------------------ list ---- */

const SOURCE_TONE: Record<ReturnSource, 'info' | 'secondary' | 'warning'> = { till: 'info', register: 'warning', office: 'secondary' };

/** "Notebook ×2 · Pen ×1 +3" — what came back, at a glance. */
function itemsText(r: ReturnRow, more: (n: number) => string): string {
  const named = (r.items ?? []).map((i) => `${i.name ?? i.code ?? '—'} ×${trimMoney(i.quantity)}`).join(' · ');
  return r.moreItems ? `${named} ${more(r.moreItems)}` : named;
}

/**
 * Issued returns (credit notes) — from the till, the web register and the
 * office. Read-only: a return can't be edited. A row opens the return itself.
 */
export function ReturnsPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dir = useLayoutDirection();
  const canCreate = usePermission('invoices.credit');
  const tenantName = useAuthStore((s) => s.tenant?.name ?? '');
  const [items, setItems] = useState<ReturnRow[] | null>(null);
  // Branch filter — a return belongs to the branch of the invoice it credits.
  const [warehouses, setWarehouses] = useState<WarehouseLite[]>([]);
  const [warehouseFilter, setWarehouseFilter] = useState('all');
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    void fetchAllReturns().then(setItems).catch(() => setItems([]));
    void api().get<{ items: WarehouseLite[] }>('/warehouses').then((r) => setWarehouses(r.data.items)).catch(() => undefined);
  }, []);

  const locale = i18n.language;
  const money = (amount: string | number, currency: string): string => formatMoney(String(amount), { locale, currency });
  const sourceLabel = (s: ReturnSource | undefined): string =>
    s === 'till' ? t('returns.source.till', { defaultValue: 'Till' })
      : s === 'register' ? t('returns.source.register', { defaultValue: 'Register' })
        : t('returns.source.office', { defaultValue: 'Office' });
  const moreLabel = (n: number): string => t('returns.moreItems', { defaultValue: '+{{n}} more', n });
  const unitsLabel = (n: string | number): string => t('returns.unitsN', { defaultValue: '{{n}} pcs', n: formatNumber(Number(n), { locale }) });

  const header = (
    <header className="flex items-start justify-between gap-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t('returns.title', { defaultValue: 'Returns / RMA' })}</h1>
        <p className="mt-1 text-sm text-fg-muted">
          {t('returns.description', { defaultValue: 'Customer returns: credit the invoice and (optionally) put the goods back in stock.' })}
        </p>
      </div>
      {canCreate && (
        <Link to="/sales/returns/new"><Button><RotateCcw className="h-4 w-4" aria-hidden />{t('returns.new', { defaultValue: 'New return' })}</Button></Link>
      )}
    </header>
  );

  if (items && items.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <EmptyState
          icon={<Undo2 className="h-8 w-8" aria-hidden />}
          title={t('returns.empty', { defaultValue: 'No returns yet' })}
          description={t('returns.emptyDescription', { defaultValue: 'Issue a return against a posted invoice to credit the customer and restock the goods.' })}
          action={canCreate ? <Link to="/sales/returns/new"><Button size="sm">{t('returns.new', { defaultValue: 'New return' })}</Button></Link> : undefined}
        />
      </div>
    );
  }

  const columns: DataTableColumn<ReturnRow>[] = [
    {
      id: 'docNumber', header: t('returns.col.number', { defaultValue: 'Return #' }),
      sortValue: (r) => r.docNumber, searchValue: (r) => r.docNumber,
      cellClassName: 'font-mono text-xs whitespace-nowrap',
      cell: (r) => <span className="font-semibold text-fg">{r.docNumber}</span>,
    },
    {
      id: 'date', header: t('returns.col.date', { defaultValue: 'Date' }),
      sortValue: (r) => r.createdAt ?? r.docDate ?? '', cellClassName: 'text-xs text-fg-muted whitespace-nowrap',
      cell: (r) => (r.createdAt ? formatDateTime(r.createdAt, { locale }) : r.docDate ? formatDate(r.docDate, { locale }) : '—'),
    },
    {
      id: 'party', header: t('returns.col.customer', { defaultValue: 'Customer' }),
      sortValue: (r) => r.partyName ?? '', searchValue: (r) => `${r.partyName ?? ''} ${r.partyCode ?? ''}`,
      cell: (r) => (
        <div className="flex flex-col">
          <span className="font-medium">{r.partyName ?? '—'}</span>
          {r.partyCode && <span className="font-mono text-[11px] text-fg-subtle">{r.partyCode}</span>}
        </div>
      ),
    },
    {
      id: 'origin', header: t('returns.col.invoice', { defaultValue: 'Original invoice' }),
      sortValue: (r) => r.originalNumber ?? '', searchValue: (r) => r.originalNumber ?? '',
      cellClassName: 'font-mono text-xs whitespace-nowrap',
      cell: (r) => (r.creditOfInvoiceId ? (
        <Link
          to={`/sales/invoices/${r.creditOfInvoiceId}`}
          onClick={(e) => e.stopPropagation()}
          className="text-brand underline-offset-4 hover:underline"
        >
          {r.originalNumber ?? '—'}
        </Link>
      ) : <span className="text-fg-muted">—</span>),
    },
    {
      id: 'items', header: t('returns.col.items', { defaultValue: 'Items' }),
      sortValue: (r) => r.unitCount ?? 0, searchValue: (r) => (r.items ?? []).map((i) => `${i.name ?? ''} ${i.code ?? ''}`).join(' '),
      cellClassName: 'max-w-[18rem]',
      cell: (r) => (
        <div className="flex flex-col">
          <span className="truncate text-sm" title={itemsText(r, moreLabel)}>{itemsText(r, moreLabel) || '—'}</span>
          <span className="text-[11px] text-fg-subtle">{unitsLabel(r.unitCount ?? 0)}</span>
        </div>
      ),
    },
    {
      id: 'source', header: t('returns.col.source', { defaultValue: 'Source' }),
      sortValue: (r) => r.source ?? '', searchValue: (r) => `${sourceLabel(r.source)} ${r.createdByName ?? ''}`,
      cell: (r) => (
        <div className="flex flex-col items-start gap-0.5">
          <Badge variant={SOURCE_TONE[r.source ?? 'office']}>{sourceLabel(r.source)}</Badge>
          {r.createdByName && <span className="text-[11px] text-fg-muted">{r.createdByName}</span>}
        </div>
      ),
    },
    {
      id: 'branch', header: t('returns.col.branch', { defaultValue: 'Branch' }),
      sortValue: (r) => r.warehouseName ?? '', searchValue: (r) => r.warehouseName ?? '',
      cellClassName: 'text-sm text-fg-muted whitespace-nowrap', cell: (r) => r.warehouseName ?? '—',
    },
    {
      id: 'restock', header: t('returns.col.restock', { defaultValue: 'Back in stock' }),
      sortValue: (r) => Number(r.restock?.units ?? -1),
      cellClassName: 'text-xs whitespace-nowrap',
      cell: (r) => (r.restock ? (
        <span className="text-fg">{unitsLabel(r.restock.units)} · <span className="text-fg-muted">{r.restock.warehouseName ?? '—'}</span></span>
      ) : <span className="text-fg-subtle">{t('returns.notRestocked', { defaultValue: 'Not restocked' })}</span>),
    },
    {
      id: 'refund', header: t('returns.col.refund', { defaultValue: 'Refund' }), align: 'end',
      sortValue: (r) => Number(r.refund?.amount ?? -1),
      cellClassName: 'whitespace-nowrap',
      cell: (r) => (r.refund ? (
        <div className="flex flex-col items-end">
          <span className="font-mono tabular-nums">{money(r.refund.amount, r.refund.currencyCode)}</span>
          <span className="font-mono text-[11px] text-fg-subtle">{r.refund.number}</span>
        </div>
      ) : <span className="text-xs text-fg-subtle">{t('returns.onAccount', { defaultValue: 'On account' })}</span>),
    },
    {
      id: 'reason', header: t('returns.col.reason', { defaultValue: 'Reason' }),
      searchValue: (r) => r.notes ?? '',
      cellClassName: 'max-w-[12rem] truncate text-xs text-fg-muted', cell: (r) => r.notes ?? '—',
    },
    {
      id: 'amount', header: t('returns.col.amount', { defaultValue: 'Credited' }), align: 'end',
      sortValue: (r) => Number(r.total ?? '0'), cellClassName: 'font-mono tabular-nums font-semibold whitespace-nowrap',
      cell: (r) => money(r.total ?? '0', r.currencyCode),
    },
  ];

  const shownItems = warehouseFilter === 'all'
    ? items ?? []
    : (items ?? []).filter((r) => (warehouseFilter === 'none' ? r.warehouseId == null : r.warehouseId === warehouseFilter));

  const credited = sumByCurrency(shownItems, (r) => ({ amount: r.total, currency: r.currencyCode }));
  const refunded = sumByCurrency(shownItems, (r) => (r.refund ? { amount: r.refund.amount, currency: r.refund.currencyCode } : null));
  const restockedUnits = shownItems.reduce((s, r) => s + Number(r.restock?.units ?? 0), 0);
  const tile = (label: string, value: string): JSX.Element => (
    <div className="flex min-w-[10rem] flex-col gap-0.5 rounded-lg border border-border bg-bg px-3 py-2">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-fg-muted">{label}</span>
      <span className="font-mono text-base font-semibold tabular-nums">{value}</span>
    </div>
  );
  const perCcy = (list: Array<{ currency: string; amount: number }>): string =>
    list.length ? list.map((v) => money(v.amount, v.currency)).join(' · ') : '—';

  /** The rows as the table holds them (search + sort), as a styled .xlsx. */
  const exportExcel = async (rows: ReturnRow[]): Promise<void> => {
    setExporting(true);
    try {
      const wh = warehouseFilter === 'all' ? null : warehouses.find((w) => w.id === warehouseFilter) ?? null;
      const n = (v: number): string => formatNumber(v, { locale });
      const columnsX = [
        { header: t('returns.col.number', { defaultValue: 'Return #' }) },
        { header: t('returns.col.date', { defaultValue: 'Date' }) },
        { header: t('returns.col.customer', { defaultValue: 'Customer' }) },
        { header: t('returns.exp.customerCode', { defaultValue: 'Customer code' }), kind: 'code' as const },
        { header: t('returns.col.invoice', { defaultValue: 'Original invoice' }) },
        { header: t('returns.col.items', { defaultValue: 'Items' }) },
        { header: t('returns.exp.units', { defaultValue: 'Units' }), kind: 'number' as const },
        { header: t('returns.col.source', { defaultValue: 'Source' }) },
        { header: t('returns.exp.by', { defaultValue: 'By' }) },
        { header: t('returns.col.branch', { defaultValue: 'Branch' }) },
        { header: t('returns.col.restock', { defaultValue: 'Back in stock' }), kind: 'number' as const },
        { header: t('returns.exp.restockedTo', { defaultValue: 'Restocked to' }) },
        { header: t('returns.exp.refundVoucher', { defaultValue: 'Refund voucher' }) },
        { header: t('returns.col.refund', { defaultValue: 'Refund' }), kind: 'money' as const },
        { header: t('returns.col.reason', { defaultValue: 'Reason' }) },
        { header: t('returns.col.amount', { defaultValue: 'Credited' }), kind: 'money' as const },
        { header: t('excel.currency') },
      ];
      const blank = (cells: Partial<Record<number, XlsxValue>>): XlsxValue[] => columnsX.map((_, i) => cells[i] ?? null);
      const sumCredit = sumByCurrency(rows, (r) => ({ amount: r.total, currency: r.currencyCode }));
      const sumRefund = sumByCurrency(rows, (r) => (r.refund ? { amount: r.refund.amount, currency: r.refund.currencyCode } : null));
      await downloadXlsx({
        fileName: `returns-${localDateStamp()}.xlsx`,
        sheetName: t('returns.title', { defaultValue: 'Returns / RMA' }),
        title: [tenantName, t('returns.title', { defaultValue: 'Returns / RMA' }), wh?.name].filter(Boolean).join(' — '),
        info: [t('excel.exportedAt', { date: formatDateTime(new Date(), { locale }), n: n(rows.length) })],
        rtl: dir === 'rtl',
        columns: columnsX,
        rows: rows.map((r) => [
          r.docNumber,
          r.createdAt ? formatDateTime(r.createdAt, { locale }) : r.docDate ?? null,
          r.partyName ?? null,
          r.partyCode ?? null,
          r.originalNumber ?? null,
          itemsText(r, moreLabel) || null,
          r.unitCount ?? null,
          sourceLabel(r.source),
          r.createdByName ?? null,
          r.warehouseName ?? null,
          r.restock ? Number(r.restock.units) : null,
          r.restock?.warehouseName ?? null,
          r.refund?.number ?? null,
          r.refund ? { money: Number(r.refund.amount), currency: r.refund.currencyCode } : null,
          r.notes ?? null,
          { money: Number(r.total ?? 0), currency: r.currencyCode },
          r.currencyCode,
        ]),
        totals: [
          blank({ 0: t('returns.exp.total', { defaultValue: 'Total · {{n}} returns', n: n(rows.length) }), 6: rows.reduce((s, r) => s + (r.unitCount ?? 0), 0) }),
          ...sumCredit.map((v) => blank({ 0: t('returns.exp.creditedIn', { defaultValue: 'Credited · {{currency}}', currency: v.currency }), 15: { money: v.amount, currency: v.currency }, 16: v.currency })),
          ...sumRefund.map((v) => blank({ 0: t('returns.exp.refundedIn', { defaultValue: 'Refunded · {{currency}}', currency: v.currency }), 13: { money: v.amount, currency: v.currency }, 16: v.currency })),
        ],
      });
    } catch {
      toast.error(t('excel.failed'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {header}
      {items && (
        <div className="flex flex-wrap gap-2">
          {tile(t('returns.tiles.count', { defaultValue: 'Returns' }), formatNumber(shownItems.length, { locale }))}
          {tile(t('returns.tiles.credited', { defaultValue: 'Credited' }), perCcy(credited))}
          {tile(t('returns.tiles.refunded', { defaultValue: 'Cash refunded' }), perCcy(refunded))}
          {tile(t('returns.tiles.units', { defaultValue: 'Units back in stock' }), formatNumber(restockedUnits, { locale }))}
        </div>
      )}
      {warehouses.length > 1 && (
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-fg-muted">{t('filters.branch', { defaultValue: 'Branch' })}</span>
            <select value={warehouseFilter} onChange={(e) => setWarehouseFilter(e.target.value)} className="h-9 rounded-md border border-border bg-bg px-2 text-sm text-fg outline-none focus:border-brand">
              <option value="all">{t('filters.allBranches', { defaultValue: 'All branches' })}</option>
              {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              <option value="none">{t('filters.noBranch', { defaultValue: 'Unbranched' })}</option>
            </select>
          </label>
        </div>
      )}
      {items === null ? (
        <Card className="p-6"><Skeleton className="h-32 w-full" /></Card>
      ) : (
      <Card className="overflow-hidden p-0">
        <DataTable
          columns={columns}
          data={shownItems}
          rowKey={(r) => r.id}
          onRowClick={(r) => navigate(`/sales/returns/${r.id}`)}
          toolbar={({ rows }) => (
            <Button variant="secondary" size="sm" onClick={() => void exportExcel(rows)} disabled={exporting || rows.length === 0}>
              <FileSpreadsheet className="h-4 w-4" aria-hidden />
              {exporting ? t('excel.exporting') : t('excel.export')}
            </Button>
          )}
        />
      </Card>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ form ---- */

/** Issue a return: pick a posted invoice, choose quantities, optionally restock. */
export function ReturnNewPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [search] = useSearchParams();

  const [invoices, setInvoices] = useState<InvoiceRow[] | null>(null);
  const [warehouses, setWarehouses] = useState<WarehouseLite[]>([]);
  const [parties, setParties] = useState<PartyLite[]>([]);
  const [invoiceId, setInvoiceId] = useState('');
  const [lines, setLines] = useState<InvoiceLine[] | null>(null);
  const [qty, setQty] = useState<Record<number, string>>({});
  const [reason, setReason] = useState('');
  const [restock, setRestock] = useState(false);
  const [warehouseId, setWarehouseId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api().get<{ items: InvoiceRow[] }>('/invoices?notes=exclude')
      .then((r) => setInvoices(r.data.items.filter((i) => RETURNABLE.has(i.status))))
      .catch(() => setInvoices([]));
    void api().get<{ items: WarehouseLite[] }>('/warehouses').then((r) => setWarehouses(r.data.items)).catch(() => undefined);
    void fetchAllParties<PartyLite>().then(setParties).catch(() => undefined);
  }, []);

  /** Add server rows to the picker — returnable ones only, no duplicates. */
  const addInvoices = (rows: InvoiceRow[]): void => {
    const keep = rows.filter((r) => RETURNABLE.has(r.status));
    if (keep.length === 0) return;
    setInvoices((prev) => {
      const byId = new Map((prev ?? []).map((i) => [i.id, i]));
      for (const r of keep) byId.set(r.id, r);
      return [...byId.values()];
    });
  };

  /**
   * Search the SERVER as the cashier types. The list loaded above is only the
   * newest 200 invoices — about two days of sales on Duhok — so a customer who
   * came back on the third day could not be credited at all: the owner's video
   * of 2026-10-27, «زانكو» returning foam boards from INV-000942 (10-22), which
   * the search never offered. The API's `q` matches the invoice number and the
   * customer's name or code, across every invoice.
   */
  const searchTimer = useRef<number | undefined>(undefined);
  const searchInvoices = (q: string): void => {
    window.clearTimeout(searchTimer.current);
    const text = q.trim();
    if (text.length < 2) return;
    searchTimer.current = window.setTimeout(() => {
      void api().get<{ items: InvoiceRow[] }>('/invoices', { params: { notes: 'exclude', q: text, limit: 100 } })
        .then((r) => addInvoices(r.data.items))
        .catch(() => undefined);
    }, 250);
  };

  // Honour ?invoice= prefill — fetched on its own when it is older than the loaded page.
  const prefillTried = useRef(false);
  useEffect(() => {
    const wanted = search.get('invoice');
    if (!wanted || !invoices) return;
    if (invoices.some((i) => i.id === wanted)) { setInvoiceId(wanted); return; }
    if (prefillTried.current) return;
    prefillTried.current = true;
    void api().get<InvoiceRow>(`/invoices/${wanted}`).then((r) => addInvoices([r.data])).catch(() => undefined);
  }, [search, invoices]);

  // Load the chosen invoice's lines.
  useEffect(() => {
    if (!invoiceId) { setLines(null); setQty({}); return; }
    void api().get<{ lines: InvoiceLine[] }>(`/invoices/${invoiceId}`)
      .then((r) => { setLines(r.data.lines ?? []); setQty({}); })
      .catch(() => setLines([]));
  }, [invoiceId]);

  const partyName = useMemo(() => {
    const m = new Map(parties.map((p) => [p.id, p]));
    return (id?: string): string => (id ? (m.get(id)?.displayName ?? '') : '');
  }, [parties]);

  const invoiceOptions = useMemo<ComboboxOption[]>(
    () => (invoices ?? []).map((i) => ({
      value: i.id,
      label: `${i.docNumber} — ${i.partyName ?? partyName(i.partyId)}`,
      keywords: `${i.docNumber} ${i.partyName ?? partyName(i.partyId)}`,
    })),
    [invoices, partyName],
  );
  const warehouseOptions = useMemo<ComboboxOption[]>(
    () => warehouses.map((w) => ({ value: w.id, label: `${w.code} — ${w.name}`, keywords: `${w.code} ${w.name}` })),
    [warehouses],
  );
  const chosen = invoices?.find((i) => i.id === invoiceId);
  const currency = chosen?.currencyCode ?? 'USD';

  const setLineQty = (lineNo: number, max: number, raw: string): void => {
    const cleaned = raw.replace(/[^0-9.]/g, '');
    const n = Number(cleaned);
    const capped = Number.isFinite(n) && n > max ? String(max) : cleaned;
    setQty((p) => ({ ...p, [lineNo]: capped }));
  };

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    const payloadLines = (lines ?? [])
      .map((l) => ({ lineNo: l.lineNo, quantity: (qty[l.lineNo] ?? '').trim() }))
      .filter((l) => Number(l.quantity) > 0);
    if (!invoiceId) { setError(t('returns.err.noInvoice', { defaultValue: 'Pick an invoice to return against.' })); return; }
    if (payloadLines.length === 0) { setError(t('returns.err.noLines', { defaultValue: 'Enter a return quantity on at least one line.' })); return; }
    if (!reason.trim()) { setError(t('returns.err.noReason', { defaultValue: 'A reason is required.' })); return; }
    if (restock && !warehouseId) { setError(t('returns.err.noWarehouse', { defaultValue: 'Choose a warehouse to restock into.' })); return; }
    setSaving(true);
    try {
      await api().post(`/invoices/${invoiceId}/credit-note`, {
        reason: reason.trim(),
        lines: payloadLines,
        restock,
        warehouseId: restock ? warehouseId : undefined,
      });
      toast.success(t('returns.created', { defaultValue: 'Return issued' }));
      navigate('/sales/returns');
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string; cause?: { code?: string } } } }).response?.data?.cause?.code;
      setError(
        code === 'credit_quantity_exceeds_remaining'
          ? t('returns.err.exceeds', { defaultValue: 'A return quantity exceeds what remains on the invoice.' })
          : t('errors:server', { defaultValue: 'Something went wrong.' }),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">{t('returns.newTitle', { defaultValue: 'New return' })}</h1>
        <p className="mt-1 text-sm text-fg-muted">{t('returns.newDescription', { defaultValue: 'Credit a posted invoice and optionally put the goods back in stock.' })}</p>
      </header>

      <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
        <Card>
          <FormField id="r-invoice" label={t('returns.invoice', { defaultValue: 'Invoice to return' })} required>
            <Combobox
              id="r-invoice"
              options={invoiceOptions}
              value={invoiceId || undefined}
              onChange={(v) => setInvoiceId(v ?? '')}
              onSearch={searchInvoices}
              placeholder={t('returns.searchInvoice', { defaultValue: 'Search a posted invoice…' })}
              ariaLabel={t('returns.invoice', { defaultValue: 'Invoice to return' })}
            />
          </FormField>
        </Card>

        {lines && lines.length > 0 && (
          <Card className="p-0">
            <table className="w-full text-start text-sm">
              <thead className="bg-bg-muted text-fg-subtle">
                <tr>
                  <th className="px-3 py-2 text-start">{t('returns.line.item', { defaultValue: 'Item' })}</th>
                  <th className="px-3 py-2 text-end w-28">{t('returns.line.sold', { defaultValue: 'Sold qty' })}</th>
                  <th className="px-3 py-2 text-end w-28">{t('returns.line.price', { defaultValue: 'Price' })}</th>
                  <th className="px-3 py-2 text-end w-32">{t('returns.line.return', { defaultValue: 'Return qty' })}</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => {
                  const sold = Number(l.quantity);
                  return (
                    <tr key={l.lineNo} className="border-t border-border">
                      <td className="px-3 py-2">
                        <div className="font-medium">{l.description ?? '—'}</div>
                        {l.unitName && <div className="text-xs text-fg-muted">{l.unitName} (×{trimMoney(l.unitFactor ?? '1')})</div>}
                      </td>
                      <td className="px-3 py-2 text-end font-mono tabular-nums text-fg-muted">{trimMoney(l.quantity)}</td>
                      <td className="px-3 py-2 text-end font-mono tabular-nums">{formatMoney(l.unitPrice, { locale: i18n.language, currency })}</td>
                      <td className="px-3 py-2">
                        <Input
                          className="text-end font-mono"
                          inputMode="decimal"
                          value={qty[l.lineNo] ?? ''}
                          placeholder="0"
                          onChange={(e) => setLineQty(l.lineNo, sold, e.target.value)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        )}

        {invoiceId && lines && lines.length === 0 && (
          <Card className="p-6 text-sm text-fg-muted">{t('returns.noLines', { defaultValue: 'This invoice has no returnable lines.' })}</Card>
        )}

        <Card className="flex flex-col gap-4">
          <FormField id="r-reason" label={t('returns.reason', { defaultValue: 'Reason' })} required>
            <textarea
              id="r-reason"
              className="block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg outline-none focus:border-brand"
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('returns.reasonPlaceholder', { defaultValue: 'e.g. damaged on arrival, wrong item…' })}
            />
          </FormField>

          <label className="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" checked={restock} onChange={(e) => setRestock(e.target.checked)} className="h-4 w-4 accent-[var(--color-brand)]" />
            <PackageOpen className="h-4 w-4 text-fg-muted" aria-hidden />
            {t('returns.restock', { defaultValue: 'Put the returned goods back in stock' })}
          </label>
          {restock && (
            <FormField id="r-warehouse" label={t('returns.warehouse', { defaultValue: 'Restock into warehouse' })} required>
              <Combobox
                id="r-warehouse"
                options={warehouseOptions}
                value={warehouseId || undefined}
                onChange={(v) => setWarehouseId(v ?? '')}
                placeholder={t('returns.searchWarehouse', { defaultValue: 'Choose a warehouse…' })}
                ariaLabel={t('returns.warehouse', { defaultValue: 'Restock into warehouse' })}
              />
            </FormField>
          )}
        </Card>

        {error && <p className="text-sm text-danger" role="alert">{error}</p>}
        <div className="flex gap-2">
          <Button type="submit" loading={saving}>{t('returns.submit', { defaultValue: 'Issue return' })}</Button>
          <Button type="button" variant="secondary" onClick={() => navigate('/sales/returns')} disabled={saving}>
            {t('actions.cancel', { defaultValue: 'Cancel' })}
          </Button>
        </div>
      </form>
    </div>
  );
}
